// Daily Autopilot page — 2026-10 redesign (ticket #288530).
//
// Layout only.  No betting / slip / parlay / X5 Rollover logic was touched.
// Every data-testid, every handler, every API call and the 30-second
// background refresh (`load(true)`) stay exactly as they were — the settings
// draft is kept in the parent so switching tabs preserves unsaved edits.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { AlertTriangle, Target, Layers, Repeat, BarChart3, Gauge, History } from "lucide-react";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import X5RolloverPanel from "./X5RolloverPanel";
import T20RolloverPanel from "./T20RolloverPanel";
import HitTrackerBadge from "../components/HitTrackerBadge";
import { LegRow } from "../components/ParlayLegRow";

const STATE_BADGE = {
  queued: { label: "Queued", cls: "bg-indigo-500/20 text-indigo-300" },
  placing: { label: "Placing", cls: "bg-amber-500/20 text-amber-300" },
  placed: { label: "Placed", cls: "bg-emerald-500/20 text-emerald-300" },
  settled: { label: "Settled", cls: "bg-emerald-600/30 text-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-500/20 text-rose-300" },
  failed: { label: "Failed", cls: "bg-rose-600/30 text-rose-200" },
  needs_review: { label: "Needs review", cls: "bg-amber-600/30 text-amber-200" },
  name_review: { label: "Name review", cls: "bg-sky-500/25 text-sky-200" },
};

const VALID_TABS = ["overview", "daily", "x5", "t20", "history"];

// ─────────────────────────────────────────────────────────────
// All leg hit-tracker + live-score fields come from the backend's
// `parlay_tracker.enrich_slip`. The <LegRow /> and <LegScoreChip />
// components live in `components/ParlayLegRow.js` so X5 / T20 /
// Autopilot History share one implementation.
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// History grouped by date → classified by Placed vs Failed.
// Backend ships `/api/autopilot/history` as a list of day buckets
// each containing `placed[]` + `failed[]`.  Today's live bets
// (from `/api/autopilot/bets`) are merged into today's bucket so
// in-flight entries show up immediately without waiting on the
// enrichment pass.
// ─────────────────────────────────────────────────────────────
const _todayUTCKey = () => new Date().toISOString().slice(0, 10);
const _PLACED_STATES = new Set([
  "placed", "settled", "won", "lost", "pushed", "void",
  "cashed_out", "awaiting_settlement",
]);

const HistoryByDate = ({ days, todayBets, renderBet }) => {
  // Merge today's live bets into the matching day bucket (replace
  // any identical ids coming from the enriched history endpoint).
  const todayKey = _todayUTCKey();
  const merged = [...(days || [])];
  const idx = merged.findIndex(d => d.date === todayKey);
  const todayPlaced = (todayBets || []).filter(b => _PLACED_STATES.has(String(b.state || "").toLowerCase()));
  const todayFailed = (todayBets || []).filter(b => !_PLACED_STATES.has(String(b.state || "").toLowerCase()));
  if (idx >= 0) {
    const existingIds = new Set((todayBets || []).map(b => b.id));
    const keep = (arr) => arr.filter(b => !existingIds.has(b.id));
    merged[idx] = {
      ...merged[idx],
      placed: [...todayPlaced, ...keep(merged[idx].placed || [])],
      failed: [...todayFailed, ...keep(merged[idx].failed || [])],
    };
    merged[idx].counts = {
      placed: merged[idx].placed.length,
      failed: merged[idx].failed.length,
      total: merged[idx].placed.length + merged[idx].failed.length,
    };
  } else if (todayBets && todayBets.length) {
    merged.unshift({
      date: todayKey,
      placed: todayPlaced,
      failed: todayFailed,
      counts: {
        placed: todayPlaced.length,
        failed: todayFailed.length,
        total: todayBets.length,
      },
    });
  }

  const totalBets = merged.reduce((s, g) => s + (g.counts?.total || 0), 0);
  const totalDays = merged.length;

  if (!totalBets) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-10 text-center text-slate-500 text-sm">
          No autopilot history yet. Once bets are placed they'll be
          archived here, grouped by date and classified as Placed or Failed.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="autopilot-history-all-time">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-white text-lg font-semibold">All-time autopilot history</h2>
          <div className="text-slate-500 text-xs">
            {totalBets} bet{totalBets === 1 ? "" : "s"} across {totalDays} day{totalDays === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {merged.map(day => (
        <HistoryDayCard key={day.date} day={day} renderBet={renderBet} isToday={day.date === todayKey} />
      ))}
    </div>
  );
};

const HistoryDayCard = ({ day, renderBet, isToday }) => {
  const [open, setOpen] = useState(isToday);
  const pretty = (() => {
    try {
      const d = new Date(`${day.date}T12:00:00Z`);
      return d.toLocaleDateString(undefined, {
        weekday: "long", year: "numeric", month: "short", day: "numeric",
      });
    } catch { return day.date; }
  })();
  const placed = day.placed || [];
  const failed = day.failed || [];
  const total = (day.counts?.total ?? (placed.length + failed.length));
  return (
    <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid={`history-day-${day.date}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-800/40 transition-colors"
        data-testid={`history-day-toggle-${day.date}`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white font-semibold">{pretty}</span>
          {isToday && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">
              Today
            </span>
          )}
          <span className="text-slate-500 text-xs font-mono">{day.date}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
            {placed.length} Placed
          </Badge>
          <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30">
            {failed.length} Failed
          </Badge>
          <span className="text-slate-500 ml-2 font-mono">
            {total} total · {open ? "▲" : "▼"}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-800 px-4 py-4 space-y-5">
          <HistorySection
            title="Placed"
            toneClass="text-emerald-300"
            bets={placed}
            emptyMsg="No bets reached Betway on this day."
            renderBet={renderBet}
            idPrefix={`placed-${day.date}`}
          />
          <HistorySection
            title="Failed"
            toneClass="text-rose-300"
            bets={failed}
            emptyMsg="Nothing failed on this day."
            renderBet={renderBet}
            idPrefix={`failed-${day.date}`}
          />
        </div>
      )}
    </Card>
  );
};

const HistorySection = ({ title, toneClass, bets, emptyMsg, renderBet, idPrefix }) => (
  <div data-testid={`history-section-${idPrefix}`}>
    <div className={`text-xs uppercase tracking-wider font-bold ${toneClass} mb-2`}>
      {title} ({bets.length})
    </div>
    {bets.length === 0 ? (
      <div className="text-slate-600 text-xs py-2">{emptyMsg}</div>
    ) : (
      <div className="space-y-3">{bets.map(renderBet)}</div>
    )}
  </div>
);

export default function AutopilotPage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesEmptyReason, setCandidatesEmptyReason] = useState(null);
  const [bets, setBets] = useState([]);
  const [historyDays, setHistoryDays] = useState([]);
  const [pnlBuckets, setPnlBuckets] = useState([]);
  const [balance, setBalance] = useState(null);
  const [x5Status, setX5Status] = useState(null); // for Overview mode cards + conflict banner
  const [refreshingBalance, setRefreshingBalance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ticking, setTicking] = useState(false);

  const auth = token ? { Authorization: `Bearer ${token}` } : {};

  // Ticket #288530 - background=true is the 30s poll: keep the page on screen
  // (no "Loading autopilot..." swap), do not overwrite settings the user is
  // editing, and do not reset the selected tab.
  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const [cfg, st, cands, bts, pnl, bal, x5, hist] = await Promise.all([
        axios.get(`${API}/autopilot/config`, { headers: auth, timeout: 30000 }),
        axios.get(`${API}/autopilot/status`, { headers: auth, timeout: 30000 }),
        axios.get(`${API}/autopilot/candidates?limit=100`, { headers: auth, timeout: 30000 }),
        axios.get(`${API}/autopilot/bets?limit=50`, { headers: auth, timeout: 30000 }),
        axios.get(`${API}/autopilot/pnl-hourly`, { headers: auth, timeout: 30000 }),
        axios.get(`${API}/autopilot/balance`, { headers: auth, timeout: 30000 }).catch(() => ({ data: null })),
        axios.get(`${API}/x5-rollover/status`, { headers: auth, timeout: 30000 }).catch(() => ({ data: null })),
        axios.get(`${API}/autopilot/history?days=90`, { headers: auth, timeout: 30000 }).catch(() => ({ data: { days: [] } })),
      ]);
      setConfig(cfg.data);
      setDraft((d) => (background && d ? d : cfg.data));
      setStatus(st.data);
      setCandidates(cands.data.items || []);
      setCandidatesEmptyReason(cands.data.empty_reason || null);
      setBets(bts.data.items || []);
      setPnlBuckets(pnl.data.buckets || []);
      setBalance(bal.data);
      setX5Status(x5.data);
      setHistoryDays(hist.data?.days || []);
    } catch (e) {
      if (!background) toast.error("Failed to load autopilot: " + (e?.response?.data?.detail || e.message));
      else console.warn("[autopilot] background refresh failed:", e?.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); const t = setInterval(() => load(true), 30000); return () => clearInterval(t); }, [load]);

  // Faster balance-only poll — surfaces header movement the instant a
  // bet settles without pulling the whole dashboard again.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/autopilot/balance`, { headers: auth });
        setBalance(data);
      } catch { /* best-effort */ }
    }, 15000);
    return () => clearInterval(t);
  }, [token]);

  // Tab <-> URL sync (preserves other query params).
  const changeTab = (next) => {
    setActiveTab(next);
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  };

  const refreshBalance = async () => {
    setRefreshingBalance(true);
    try {
      const { data } = await axios.post(`${API}/autopilot/balance/refresh`, {}, { headers: auth });
      setBalance(data);
      if (data?.balance != null) {
        toast.success(`Balance: ${data.currency} ${data.balance.toFixed(2)}`);
      } else {
        toast.info(data?.message || "Balance refresh returned no value");
      }
    } catch (e) {
      toast.error("Balance refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRefreshingBalance(false);
    }
  };

  const toggleEnabled = async (checked) => {
    try {
      const { data } = await axios.post(`${API}/autopilot/config/enable`,
        { enabled: !!checked }, { headers: auth });
      setConfig(data); setDraft(data);
      toast.success(checked ? "Autopilot ARMED" : "Autopilot stopped");
      load();
    } catch (e) {
      toast.error("Toggle failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const saveConfig = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/autopilot/config`, draft, { headers: auth });
      setConfig(data); setDraft(data);
      toast.success("Autopilot config saved");
      load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const manualTick = async () => {
    setTicking(true);
    try {
      const { data } = await axios.post(`${API}/autopilot/tick`, {}, { headers: auth });
      if (data.placed_bet_id) {
        const c = data.candidate || {};
        toast.success(`Queued ${c.home_team} vs ${c.away_team} / ${c.market_type} ${c.pick_label}`);
      } else {
        toast.info("Tick skipped: " + (data.skip_reason || "no candidate"));
      }
      load();
    } catch (e) {
      toast.error("Tick failed: " + (e?.response?.data?.detail || e.message));
    } finally { setTicking(false); }
  };

  // ─── Needs-review resolver (ticket #288530 follow-up) ────────────
  // Two buttons on every needs_review row: "Bet was placed" /
  // "No bet was placed". Both pop a confirm reminding the user to
  // check Betway's My Bets first. "Not placed" marks the ticket
  // REJECTED (frees dedup + unblocks today's autopilot). "Placed"
  // marks it PLACED so the normal settler takes over.
  const [reviewTarget, setReviewTarget] = useState(null); // { bet, outcome }
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // ─── Name-review resolver (ticket 288530-NR) ─────────────────────
  // Clicking a "Name review" row opens a dialog asking, per team,
  // whether our team name matches a Betway name from that page.
  // The user picks the matching Betway name from a dropdown of the
  // games the bot saw, or types the exact Betway name in a free
  // text box.  Confirmed pairs are stored in a shared
  // team_name_aliases collection, then the bet is retried once if
  // kickoff is still further out than the user's kickoff lead.
  const [nameReviewBet, setNameReviewBet] = useState(null);
  // Re-run with new rules: holds { bet, preview, loading, confirming }
  // while the dialog is open.
  const [rerunTarget, setRerunTarget] = useState(null);
  // Per-leg selections. For single-leg bets we still populate a 1-item array
  // using the top-level `request` + `candidate_games` so the UI code paths
  // merge. Each item: { home, away, homeMode, awayMode }.
  const [nameReviewLegSel, setNameReviewLegSel] = useState([]);
  const [nameReviewSubmitting, setNameReviewSubmitting] = useState(false);

  // Derive the list of review legs for a bet. Parlays expose
  // `name_review_legs[]` (each with home_team/away_team/market_label/pick/
  // candidate_games). Single-match bets expose a top-level `request` +
  // `candidate_games` — we wrap that into a 1-item array so one render path
  // handles both shapes.
  const getNameReviewLegs = (bet) => {
    if (!bet) return [];
    if (Array.isArray(bet.name_review_legs) && bet.name_review_legs.length) {
      return bet.name_review_legs;
    }
    const req = bet.request || {};
    return [{
      home_team: req.home_team,
      away_team: req.away_team,
      market_label: req.market_label,
      pick: req.pick,
      candidate_games: bet.candidate_games || [],
    }];
  };

  const openRerunPreview = async (bet) => {
    setRerunTarget({ bet, preview: null, loading: true, confirming: false });
    try {
      const { data } = await axios.post(
        `${API}/autopilot/parlay/${bet.id}/rerun-preview`,
        {},
        { headers: auth },
      );
      setRerunTarget({ bet, preview: data, loading: false, confirming: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setRerunTarget(null);
    }
  };

  const confirmRerun = async () => {
    if (!rerunTarget) return;
    setRerunTarget({ ...rerunTarget, confirming: true });
    try {
      const { data } = await axios.post(
        `${API}/autopilot/parlay/${rerunTarget.bet.id}/rerun-confirm`,
        {},
        { headers: auth },
      );
      toast.success(
        `New slip queued · ${data.legs?.length || 0} legs · odds ${(data.combined_odds || 0).toFixed(2)}`,
      );
      setRerunTarget(null);
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-run failed");
      setRerunTarget({ ...rerunTarget, confirming: false });
    }
  };

  const openNameReview = (bet) => {
    setNameReviewBet(bet);
    const legs = getNameReviewLegs(bet);
    setNameReviewLegSel(legs.map(() => ({
      home: "", away: "", homeMode: "dropdown", awayMode: "dropdown",
    })));
  };

  const updateLegSel = (idx, patch) => {
    setNameReviewLegSel(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const confirmNameReview = async () => {
    if (!nameReviewBet) return;
    const legs = getNameReviewLegs(nameReviewBet);
    const aliases = [];
    legs.forEach((leg, i) => {
      const sel = nameReviewLegSel[i] || {};
      if (sel.home && sel.home !== leg.home_team) {
        aliases.push({ our_name: leg.home_team, betway_name: sel.home });
      }
      if (sel.away && sel.away !== leg.away_team) {
        aliases.push({ our_name: leg.away_team, betway_name: sel.away });
      }
    });
    if (!aliases.length) {
      const anyFilled = nameReviewLegSel.some(s => s.home || s.away);
      if (!anyFilled) {
        toast.error("Pick or type the Betway spelling for at least one team.");
        return;
      }
      // The user confirmed every name they filled already matches — just
      // retry without a new alias. Send a trivial pair so the backend
      // proceeds; it strips identical pairs itself.
      const firstLeg = legs[0] || {};
      aliases.push({ our_name: firstLeg.home_team || "x", betway_name: firstLeg.home_team || "x" });
    }
    setNameReviewSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/autopilot/bets/${nameReviewBet.id}/resolve-name-review`,
        { aliases },
        { headers: auth },
      );
      if (data.retried) {
        toast.success(`Saved ${data.aliases_saved?.length || 0} alias(es) — retrying bet.`);
      } else {
        toast.info(data.message || "kicked off before review");
      }
      setNameReviewBet(null);
      load();
    } catch (e) {
      toast.error("Resolve failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setNameReviewSubmitting(false);
    }
  };

  const confirmReview = async () => {
    if (!reviewTarget) return;
    const { bet, outcome } = reviewTarget;
    setReviewSubmitting(true);
    try {
      await axios.post(
        `${API}/autopilot/bets/${bet.id}/resolve-review`,
        { outcome },
        { headers: auth },
      );
      toast.success(
        outcome === "placed"
          ? "Marked as placed — settlement will track it."
          : "Marked as not placed — autopilot can resume today."
      );
      setReviewTarget(null);
      load();
    } catch (e) {
      toast.error("Resolve failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <TopNav />
        <div className="p-10 text-slate-300">Loading autopilot…</div>
      </div>
    );
  }

  const s = status?.summary || {};
  const running = !!status?.running;
  const dailyOn = !!config?.enabled;
  const parlayOn = !!config?.parlay_mode_enabled;
  const x5Cfg = x5Status?.config || {};
  const x5Today = x5Status?.today || null;
  const x5On = !!x5Cfg.enabled;
  const bothOn = dailyOn && x5On;

  // Shared bet-card renderer used by every day bucket on the
  // History tab.  Hoisted here so needs_review / name_review handlers
  // (which live on the component) are in scope.
  const renderBetCard = (b) => {
    const req = b.request || {};
    const sb = STATE_BADGE[b.state] || { label: b.state, cls: "bg-slate-700 text-slate-200" };
    const isReview = b.state === "needs_review";
    const isNameReview = b.state === "name_review";
    const hasRequest = !!(req && (req.home_team || req.market_label));
    const legs = Array.isArray(b.legs) ? b.legs : [];
    const isParlay = !hasRequest && legs.length > 0;
    const slipTracker = b.slip_tracker || (isParlay ? "pending" : null);
    const createdTime = (b.created_at || "").slice(11, 19);
    const oddsDisplay = isParlay ? (b.combined_odds ?? b.odds) : b.odds;
    // Re-run eligibility — mirrors _RERUN_ELIGIBLE_STAGES on the backend
    // so the button is only painted when the backend would accept it.
    const RERUN_STAGES = new Set([
      "incomplete_slip", "slip_leg_missing", "name_review",
      "open_login", "timeout",
    ]);
    const stageLower = (b.stage || "").toLowerCase();
    const messageLower = (b.message || "").toLowerCase();
    const stageIsTimeout = stageLower.includes("timeout") || messageLower.includes("timed out");
    const alreadyRerun = !!b.manual_rerun_slip_id;
    const canRerun = (
      isParlay &&
      b.state === "failed" &&
      !isReview && !isNameReview &&
      (RERUN_STAGES.has(stageLower) || stageIsTimeout) &&
      !alreadyRerun
    );
    return (
      <div
        key={b.id}
        className="rounded-xl border border-slate-800 bg-slate-950/60 overflow-hidden"
        data-testid={`autopilot-bet-card-${b.id}`}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-900/80 border-b border-slate-800 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={sb.cls}>{sb.label}</Badge>
            {isParlay ? (
              <>
                <span className="text-slate-400 text-xs uppercase tracking-wider">
                  {legs.length}-leg parlay
                </span>
                {slipTracker && <HitTrackerBadge tracker={slipTracker} />}
              </>
            ) : hasRequest ? (
              <span className="text-white text-sm font-medium">
                {req.home_team} <span className="text-slate-500">vs</span> {req.away_team}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <div><span className="text-slate-500">Stake</span> <span className="text-amber-300 font-semibold">${b.stake}</span></div>
            <div><span className="text-slate-500">Odds</span> <span className="text-slate-200 font-semibold">{oddsDisplay ?? "—"}</span></div>
            <div className="text-slate-500 font-mono">{createdTime} UTC</div>
            {(isReview || isNameReview || canRerun) && (
              <div className="inline-flex gap-1.5">
                {isReview && (
                  <>
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setReviewTarget({ bet: b, outcome: "placed" })}
                      data-testid={`review-placed-btn-${b.id}`}
                    >
                      Bet was placed
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                      onClick={() => setReviewTarget({ bet: b, outcome: "not_placed" })}
                      data-testid={`review-not-placed-btn-${b.id}`}
                    >
                      No bet was placed
                    </Button>
                  </>
                )}
                {isNameReview && (
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={() => openNameReview(b)}
                    data-testid={`name-review-btn-${b.id}`}
                  >
                    Review names
                  </Button>
                )}
                {canRerun && (
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => openRerunPreview(b)}
                    data-testid={`rerun-btn-${b.id}`}
                  >
                    Re-run with new rules
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-3">
          {isParlay ? (
            <ul className="divide-y divide-slate-800/70" data-testid={`autopilot-bet-legs-${b.id}`}>
              {legs.map((l, i) => (
                <LegRow key={i} leg={l} />
              ))}
            </ul>
          ) : hasRequest ? (
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="text-emerald-300">
                {req.market_label} <span className="text-slate-500">/</span> {req.pick}
              </div>
            </div>
          ) : (
            <div className="text-slate-500 text-xs">No match detail on this bet.</div>
          )}
          {(b.message || b.stage) && (
            <div className="text-slate-500 text-xs mt-2 pt-2 border-t border-slate-800/70">
              {b.message || b.stage}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-24 space-y-10">
        {/* ── Page header — v3 Linear-clean, master switch to the right ── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div
              className="text-[11px] uppercase inline-flex items-center gap-2 mb-3"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.14em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              <span
                className="h-1 w-1 rounded-full"
                style={{ background: running ? "#22c55e" : "var(--v3-text-4)" }}
              />
              Autopilot · {status?.today}
            </div>
            <h1
              className="text-4xl md:text-5xl"
              data-testid="autopilot-title"
              style={{ fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05 }}
            >
              Daily Autopilot
            </h1>
            <p
              className="mt-4 max-w-xl text-[14px] leading-relaxed"
              style={{ color: "var(--v3-text-2)" }}
            >
              Scans today's schedule every {config?.scan_interval_minutes || 15}&nbsp;min, sizes each
              stake with Kelly, stops at ${config?.daily_loss_stop} daily loss.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span
              className="text-[11px] uppercase inline-flex items-center gap-1.5"
              style={{
                color: running ? "#22c55e" : "var(--v3-text-3)",
                letterSpacing: "0.12em",
                fontFamily: "var(--v3-font-mono)",
                fontWeight: 500,
              }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  background: running ? "#22c55e" : "var(--v3-text-4)",
                  boxShadow: running ? "0 0 6px #22c55e" : "none",
                }}
              />
              {running ? "Running" : "Stopped"}
            </span>
            <Switch
              checked={!!config?.enabled}
              onCheckedChange={toggleEnabled}
              data-testid="autopilot-master-switch"
            />
          </div>
        </div>

        {/* ── Tabs — v3 hairline-underline rail ── */}
        <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide">
            <TabsList
              className="h-auto w-full justify-start bg-transparent border-0 p-0 inline-flex gap-8 rounded-none"
              style={{ borderBottom: "1px solid var(--v3-line)" }}
              data-testid="autopilot-tabs"
            >
              <TabsTrigger
                value="overview"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] gap-1.5 text-[color:var(--v3-text-3)] data-[state=active]:text-[color:var(--v3-text)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="autopilot-tab-overview"
              >
                <Gauge className="h-[14px] w-[14px]" strokeWidth={1.75} /> Overview
              </TabsTrigger>
              <TabsTrigger
                value="daily"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] gap-1.5 text-[color:var(--v3-text-3)] data-[state=active]:text-[color:var(--v3-text)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="autopilot-tab-daily"
              >
                <Target className="h-[14px] w-[14px]" strokeWidth={1.75} /> Daily
              </TabsTrigger>
              <TabsTrigger
                value="x5"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] gap-1.5 text-[color:var(--v3-text-3)] data-[state=active]:text-[color:var(--v3-text)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="autopilot-tab-x5"
              >
                <Repeat className="h-[14px] w-[14px]" strokeWidth={1.75} /> X5
              </TabsTrigger>
              <TabsTrigger
                value="t20"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] gap-1.5 text-[color:var(--v3-text-3)] data-[state=active]:text-[color:var(--v3-text)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="autopilot-tab-t20"
              >
                <Repeat className="h-[14px] w-[14px]" strokeWidth={1.75} /> T20
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] gap-1.5 text-[color:var(--v3-text-3)] data-[state=active]:text-[color:var(--v3-text)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="autopilot-tab-history"
              >
                <History className="h-4 w-4" /> History
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ─────────────────────── OVERVIEW ─────────────────────── */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            {status?.stop_reason && !running ? (
              <Card className="bg-rose-950/30 border-rose-500/40">
                <CardContent className="py-3 px-4 text-rose-300 text-sm" data-testid="autopilot-stop-reason">
                  ⛔ Autopilot stopped: {status.stop_reason}
                </CardContent>
              </Card>
            ) : null}

            {bothOn ? (
              <Card className="bg-amber-950/30 border-amber-500/40" data-testid="autopilot-conflict-banner">
                <CardContent className="py-3 px-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <div className="text-amber-200 text-sm">
                    <div className="font-semibold">Both Daily Autopilot and X5 Rollover are ON.</div>
                    <div className="text-amber-300/80 mt-0.5">
                      They share the Betway slip and can compete for the same stake. Keep one on at a time
                      unless you're intentionally running them in parallel.
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Betway balance (unchanged content, same testids) */}
            <Card className="bg-gradient-to-br from-emerald-950/50 to-slate-900 border-emerald-500/30" data-testid="betway-balance-widget">
              <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-emerald-300 text-xs uppercase tracking-wider font-semibold">Live Betway Balance</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-white text-4xl font-bold tabular-nums" data-testid="betway-balance-value">
                      {balance?.balance != null ? `${balance.currency || "$"} ${Number(balance.balance).toFixed(2)}` : "— —"}
                    </span>
                    {balance?.updated_at ? (
                      <span className="text-slate-400 text-xs" data-testid="betway-balance-updated">
                        · {new Date(balance.updated_at).toLocaleTimeString()}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-slate-400 text-xs mt-1">
                    {balance?.source === "auto_bet_placement"
                      ? "Captured right after your last auto-bet"
                      : balance?.source === "refresh"
                      ? "Live-scraped from Betway header"
                      : balance?.message || "Place a bet to seed, or refresh now"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshBalance}
                  disabled={refreshingBalance}
                  data-testid="betway-balance-refresh-btn"
                >
                  {refreshingBalance ? "Reading…" : "Refresh"}
                </Button>
              </CardContent>
            </Card>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4">
                <div className="text-slate-400 text-xs">Queued today</div>
                <div className="text-white text-2xl font-bold" data-testid="autopilot-kpi-queued">{s.bets_queued || 0}</div>
              </CardContent></Card>
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4">
                <div className="text-slate-400 text-xs">Placed</div>
                <div className="text-emerald-300 text-2xl font-bold" data-testid="autopilot-kpi-placed">{s.bets_placed || 0}</div>
              </CardContent></Card>
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4">
                <div className="text-slate-400 text-xs">Realised P&amp;L</div>
                <div className={`text-2xl font-bold ${(s.realised_pnl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="autopilot-kpi-pnl">
                  ${(s.realised_pnl || 0).toFixed(2)}
                </div>
              </CardContent></Card>
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4">
                <div className="text-slate-400 text-xs">Daily loss (24h)</div>
                <div className="text-amber-300 text-2xl font-bold" data-testid="autopilot-kpi-loss">
                  ${(s.daily_realised_loss || 0).toFixed(2)}
                </div>
                <div className="text-slate-500 text-xs">of ${config?.daily_loss_stop} stop</div>
              </CardContent></Card>
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4">
                <div className="text-slate-400 text-xs">Settled</div>
                <div className="text-white text-2xl font-bold" data-testid="autopilot-kpi-settled">
                  {(s.bets_settled_won || 0) + (s.bets_settled_lost || 0) + (s.bets_settled_void || 0)}
                </div>
                <div className="text-slate-500 text-xs">W {s.bets_settled_won || 0} · L {s.bets_settled_lost || 0}</div>
              </CardContent></Card>
            </div>

            {/* Mode status cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ModeStatusCard
                testid="mode-single"
                icon={<Target className="h-4 w-4" />}
                title="Single bets"
                on={dailyOn && !parlayOn}
                onLabel="On"
                offLabel={dailyOn ? "Off (parlay mode active)" : "Off"}
                detail={
                  dailyOn && !parlayOn
                    ? `${s.bets_placed || 0} placed · $${(s.realised_pnl || 0).toFixed(2)} today`
                    : "Flip the master switch and keep Parlay mode OFF to arm."
                }
              />
              <ModeStatusCard
                testid="mode-parlay"
                icon={<Layers className="h-4 w-4" />}
                title="Parlay mode"
                on={dailyOn && parlayOn}
                onLabel={`On · ${config?.parlay_legs ?? 3}-leg slips`}
                offLabel={parlayOn ? "Armed (master off)" : "Off"}
                detail={
                  parlayOn
                    ? `Stake $${Number(config?.parlay_stake ?? 0).toFixed(2)} / slip · one slip counts as one bet`
                    : "Off by default. 2–4 legs from different matches."
                }
              />
              <ModeStatusCard
                testid="mode-x5"
                icon={<Repeat className="h-4 w-4" />}
                title="X5 Rollover"
                on={x5On}
                onLabel={x5Today?.state ? `On · ${x5Today.state}` : "On"}
                offLabel="Off"
                detail={
                  x5Today
                    ? `${x5Today.date || ""} · stake $${Number(x5Today.stake ?? x5Cfg.current_stake ?? 0).toFixed(2)}`
                    : x5On
                      ? `Next stake $${Number(x5Cfg.current_stake ?? x5Cfg.starting_stake ?? 0).toFixed(2)}`
                      : "DC + HT 5-pick cascade. Independent of Parlay mode."
                }
              />
            </div>

            <PnlChart buckets={pnlBuckets} />
          </TabsContent>

          {/* ─────────────────────── DAILY AUTOPILOT ─────────────────────── */}
          <TabsContent value="daily" className="space-y-6 mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white text-lg">Configuration</CardTitle>
                  <div className="text-xs text-slate-500 mt-1">
                    Scan interval, Kelly fraction, daily caps. Edits stay on screen while tabs switch — click Save to persist.
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={manualTick} disabled={ticking || !config?.enabled}
                    data-testid="autopilot-manual-tick">
                    {ticking ? "Ticking…" : "Run tick now"}
                  </Button>
                  <Button size="sm" onClick={saveConfig} disabled={saving} data-testid="autopilot-save-config">
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FormNum label="Min confidence (%)" value={draft?.min_confidence} onChange={v => setDraft({ ...draft, min_confidence: v })} testid="cfg-min-conf" />
                <FormNum label="Kelly fraction" value={draft?.kelly_fraction} step={0.05} onChange={v => setDraft({ ...draft, kelly_fraction: v })} testid="cfg-kelly" />
                <FormNum label="Bankroll ($)" value={draft?.bankroll} onChange={v => setDraft({ ...draft, bankroll: v })} testid="cfg-bankroll" />
                <FormNum label="Min stake ($)" value={draft?.min_stake} step={0.5} onChange={v => setDraft({ ...draft, min_stake: v })} testid="cfg-min-stake" />
                <FormNum label="Max stake ($)" value={draft?.max_stake} step={0.5} onChange={v => setDraft({ ...draft, max_stake: v })} testid="cfg-max-stake" />
                <FormNum label="Daily profit target ($)" value={draft?.daily_profit_target} onChange={v => setDraft({ ...draft, daily_profit_target: v })} testid="cfg-profit-target" />
                <FormNum label="Daily loss stop ($)" value={draft?.daily_loss_stop} onChange={v => setDraft({ ...draft, daily_loss_stop: v })} testid="cfg-loss-stop" />
                <FormNum label="Max bets / day" value={draft?.max_bets_per_day} step={1} onChange={v => setDraft({ ...draft, max_bets_per_day: v })} testid="cfg-max-bets" />
                <FormNum label="Scan interval (min)" value={draft?.scan_interval_minutes} step={1} onChange={v => setDraft({ ...draft, scan_interval_minutes: v })} testid="cfg-scan-min" />
                <div className="flex items-center gap-3 pt-6" data-testid="cfg-live-toggle">
                  <Switch
                    checked={!!draft?.include_live_fixtures}
                    onCheckedChange={v => setDraft({ ...draft, include_live_fixtures: !!v })}
                  />
                  <Label className="text-slate-300 text-sm cursor-pointer">
                    Scan live fixtures too <span className="text-slate-500">(in-play)</span>
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Parlay mode — its own clearly separated section below Config */}
            <Card className="bg-slate-900 border-slate-800" data-testid="autopilot-parlay-card">
              <CardHeader className="flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Layers className="h-4 w-4 text-indigo-300" /> Parlay mode
                  </CardTitle>
                  <div className="text-xs text-slate-500 mt-1 max-w-xl">
                    Off by default. When ON, each scan places one slip of 2–4 picks from different matches
                    instead of a single bet. One slip counts as one bet toward the daily cap. If any leg
                    can't be found on Betway the whole slip is skipped.
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="cfg-parlay-toggle">
                  <Switch
                    checked={!!draft?.parlay_mode_enabled}
                    onCheckedChange={v => setDraft({ ...draft, parlay_mode_enabled: !!v })}
                  />
                  <Label className="text-slate-300 text-sm cursor-pointer">
                    {draft?.parlay_mode_enabled ? "ON" : "OFF"}
                  </Label>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FormNum
                  label="Legs per slip (2–4)"
                  value={draft?.parlay_legs}
                  step={1}
                  min={2}
                  max={4}
                  onChange={v => {
                    const n = Math.max(2, Math.min(4, Math.round(Number(v) || 3)));
                    setDraft({ ...draft, parlay_legs: n });
                  }}
                  testid="cfg-parlay-legs"
                  disabled={!draft?.parlay_mode_enabled}
                />
                <FormNum
                  label="Stake per slip ($)"
                  value={draft?.parlay_stake}
                  step={0.5}
                  onChange={v => setDraft({ ...draft, parlay_stake: v })}
                  testid="cfg-parlay-stake"
                  disabled={!draft?.parlay_mode_enabled}
                />
              </CardContent>
            </Card>

            {/* Candidates preview */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">
                  Next picks preview <span className="text-slate-400 text-sm">({candidates.length} shown, click "Save" after changing filters)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-400 text-left">
                    <tr className="border-b border-slate-800">
                      <th className="p-2">#</th><th>Match</th><th>League</th><th>Pick</th>
                      <th>Conf</th><th>Odds</th><th>Stake</th><th>Score</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, i) => (
                      <tr key={c.dedup_key} className="border-b border-slate-800/50 hover:bg-slate-800/40">
                        <td className="p-2 text-slate-500">{i + 1}</td>
                        <td className="p-2 text-white">{c.home_team} <span className="text-slate-500">vs</span> {c.away_team}</td>
                        <td className="p-2 text-slate-400">{c.league}</td>
                        <td className="p-2 text-emerald-300">{c.market_type} / {c.pick_label}</td>
                        <td className="p-2 text-white">{c.confidence?.toFixed(1)}%</td>
                        <td className="p-2 text-slate-300">{c.estimated_odds?.toFixed(2)}</td>
                        <td className="p-2 text-amber-300">${c.stake?.toFixed(2)}</td>
                        <td className="p-2 text-slate-400">{c.score?.toFixed(1)}</td>
                        <td className="p-2">
                          {c.already_queued
                            ? <Badge className="bg-slate-700 text-slate-300">queued</Badge>
                            : <Badge className="bg-indigo-500/20 text-indigo-300">eligible</Badge>}
                        </td>
                      </tr>
                    ))}
                    {!candidates.length && (
                      <tr><td colSpan={9} className="p-6 text-center text-slate-500">
                        {candidatesEmptyReason
                          ? candidatesEmptyReason
                          : "No eligible candidates — lower min_confidence or widen allowed markets."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────────────── X5 ROLLOVER ─────────────────────── */}
          <TabsContent value="x5" className="space-y-6 mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-indigo-300" /> X5 Rollover
                  </CardTitle>
                  <div className="text-xs text-slate-500 mt-1 max-w-xl">
                    DC + HT 5-pick cascade on a fixed schedule. Independent of Daily Autopilot and Parlay mode.
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <X5RolloverPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────────────── T20 ROLLOVER ─────────────────────── */}
          <TabsContent value="t20" className="space-y-6 mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-amber-300" /> Top 20 Elite Rollover
                  </CardTitle>
                  <div className="text-xs text-slate-500 mt-1 max-w-xl">
                    One parlay a day built from today's Top 20 Elite (New) list, then filtered through
                    Betway's supported-leagues verdict. Separate config, cycle, days and history from
                    X5 — turning this on does not affect X5 or Daily Autopilot.
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <T20RolloverPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─────────────────────── HISTORY ─────────────────────── */}
          <TabsContent value="history" className="space-y-4 mt-4">
            <HistoryByDate
              days={historyDays}
              todayBets={bets}
              renderBet={renderBetCard}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirm dialog shared by both "Bet was placed" and "No bet was
          placed" actions on any Needs-review bet row. Reminds the user
          to check Betway's My Bets first — the whole point of the
          Needs-review state is Betway may have accepted the bet without
          confirming it to the autopilot. */}
      <AlertDialog
        open={!!reviewTarget}
        onOpenChange={(o) => { if (!o && !reviewSubmitting) setReviewTarget(null); }}
      >
        <AlertDialogContent
          className="bg-slate-900 border-slate-800 text-slate-100"
          data-testid="review-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Check Betway My Bets first
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 text-sm space-y-2">
              <div>
                Open <span className="text-amber-300 font-semibold">My Bets</span> on
                Betway and confirm whether this slip actually went through before
                clicking either option — getting it wrong here will either leave a
                real bet untracked or let autopilot place a second bet on top of it.
              </div>
              {reviewTarget && (
                <div className="pt-2 text-slate-300">
                  You are marking this bet as{" "}
                  <span className={
                    reviewTarget.outcome === "placed"
                      ? "text-emerald-300 font-semibold"
                      : "text-rose-300 font-semibold"
                  }>
                    {reviewTarget.outcome === "placed" ? "placed on Betway" : "NOT placed"}
                  </span>.
                  {reviewTarget.outcome === "not_placed" && (
                    <div className="text-slate-500 text-xs mt-1">
                      The match is freed for retry and Daily Autopilot, X5 Rollover
                      and Top-20 Rollover can carry on today.
                    </div>
                  )}
                  {reviewTarget.outcome === "placed" && (
                    <div className="text-slate-500 text-xs mt-1">
                      It will be tracked and settled like any other placed bet.
                    </div>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              data-testid="review-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReview}
              disabled={reviewSubmitting}
              className={
                reviewTarget?.outcome === "placed"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-rose-600 hover:bg-rose-700 text-white"
              }
              data-testid="review-confirm-btn"
            >
              {reviewSubmitting
                ? "Working…"
                : reviewTarget?.outcome === "placed"
                  ? "Yes, it was placed"
                  : "Yes, it was NOT placed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-run dialog — previews the rebuilt slip (kept + refill
          legs, combined odds, stake) and asks for explicit
          confirmation before placing exactly one new slip linked
          back to the original.  All retry logic is manual; no
          automatic re-tries fire from here. */}
      <Dialog open={!!rerunTarget} onOpenChange={(o) => { if (!o && !(rerunTarget?.confirming)) setRerunTarget(null); }}>
        <DialogContent
          className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl"
          data-testid="rerun-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Re-run with new rules</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Keeps the legs that were already added to Betway, drops the
              failed ones, and refills from the next-best games (different
              matches, Betway-available, kickoff lead respected). No bet
              is placed until you confirm.
            </DialogDescription>
          </DialogHeader>

          {rerunTarget?.loading && (
            <div className="text-slate-400 text-sm py-6 text-center">Building rebuild preview…</div>
          )}

          {rerunTarget?.preview && (() => {
            const p = rerunTarget.preview;
            const kept = p.kept_legs || [];
            const refill = p.refill_candidates || [];
            const total = kept.length + refill.length;
            const stake = p.stake ?? p.original?.stake;
            const odds = p.combined_odds;
            const statusTone =
              p.status === "ready"
                ? "text-emerald-300"
                : p.status === "may_have_placed"
                ? "text-amber-300"
                : "text-rose-300";
            const statusLabel = {
              ready: `Ready — ${total} legs · odds ${(odds || 0).toFixed(2)} · stake $${stake}`,
              may_have_placed: "Blocked — Check Betway My Bets first",
              already_rerun: "Blocked — slip has already been re-run once",
              not_enough_candidates: "Blocked — not enough eligible refill games",
              ineligible: "Blocked — slip is not re-runnable",
              not_found: "Blocked — slip not found",
            }[p.status] || p.status;

            return (
              <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
                <div className={`text-sm font-semibold ${statusTone}`} data-testid="rerun-status">
                  {statusLabel}
                </div>
                {p.reason && (
                  <div className="text-slate-400 text-xs bg-slate-800/40 border border-slate-700 rounded px-3 py-2">
                    {p.reason}
                  </div>
                )}

                {kept.length > 0 && (
                  <div data-testid="rerun-kept-section">
                    <div className="text-xs uppercase tracking-wider font-bold text-emerald-300 mb-1">
                      Kept legs ({kept.length})
                    </div>
                    <ul className="divide-y divide-slate-800/70">
                      {kept.map((lg, i) => (
                        <li key={`k-${i}`} className="py-2 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-white">{lg.home_team} <span className="text-slate-500">vs</span> {lg.away_team}</span>
                            <span className="text-slate-500 text-xs">{lg.odds != null ? `@ ${Number(lg.odds).toFixed(2)}` : ""}</span>
                          </div>
                          <div className="text-emerald-300 text-xs mt-0.5">{lg.market_label} <span className="text-slate-500">/</span> {lg.pick}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {refill.length > 0 && (
                  <div data-testid="rerun-refill-section">
                    <div className="text-xs uppercase tracking-wider font-bold text-amber-300 mb-1">
                      Refill legs ({refill.length})
                    </div>
                    <ul className="divide-y divide-slate-800/70">
                      {refill.map((lg, i) => (
                        <li key={`r-${i}`} className="py-2 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-white">{lg.home_team} <span className="text-slate-500">vs</span> {lg.away_team}</span>
                            <span className="text-slate-500 text-xs">
                              {lg.odds != null ? `@ ${Number(lg.odds).toFixed(2)}` : ""}
                              {lg.availability === true && <span className="text-emerald-400 ml-2">· Betway ✓</span>}
                              {lg.availability === null && <span className="text-slate-500 ml-2">· Betway ?</span>}
                            </span>
                          </div>
                          <div className="text-amber-300 text-xs mt-0.5">{lg.market_label} <span className="text-slate-500">/</span> {lg.pick}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(p.failed_matches) && p.failed_matches.length > 0 && (
                  <div className="text-xs text-rose-300" data-testid="rerun-failed-matches">
                    <span className="font-semibold">Dropped failed legs: </span>
                    {p.failed_matches.map(([h, a], i) => (
                      <span key={i} className="mr-2">{h} vs {a}</span>
                    ))}
                  </div>
                )}

                {p.status === "ready" && (
                  <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 flex items-center justify-between">
                    <div className="text-sm text-slate-200">
                      <span className="text-slate-500 mr-2">Combined odds</span>
                      <span className="text-white font-semibold">{(odds || 0).toFixed(2)}</span>
                      <span className="text-slate-500 mx-2">·</span>
                      <span className="text-slate-500 mr-2">Stake</span>
                      <span className="text-amber-300 font-semibold">${stake}</span>
                      <span className="text-slate-500 mx-2">·</span>
                      <span className="text-slate-500 mr-2">Potential return</span>
                      <span className="text-emerald-300 font-semibold">${((odds || 0) * (stake || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="text-slate-300 border-slate-700 hover:bg-slate-800"
              onClick={() => setRerunTarget(null)}
              disabled={rerunTarget?.confirming}
              data-testid="rerun-cancel-btn"
            >
              Cancel
            </Button>
            {rerunTarget?.preview?.status === "ready" && (
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={confirmRerun}
                disabled={rerunTarget?.confirming}
                data-testid="rerun-confirm-btn"
              >
                {rerunTarget?.confirming ? "Placing…" : "Confirm & place slip"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Name Review dialog — ticket 288530-NR.  Shown when the user
          clicks "Review names" on a NAME_REVIEW ticket.  Lists every
          game Betway rendered on the league page the bot searched and
          asks "Is <our name> the same as <Betway name>?" for each
          side.  The user may pick from the dropdown or type the
          exact Betway name in the free-text box. */}
      <Dialog open={!!nameReviewBet} onOpenChange={(o) => { if (!o && !nameReviewSubmitting) setNameReviewBet(null); }}>
        <DialogContent
          className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl"
          data-testid="name-review-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Confirm the Betway team names</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Betway couldn't find one or more teams on the pages the bot
              searched. For each match below, pick the Betway-side spelling
              from the games Betway showed on that league page, or type it
              in. Confirmed pairs are saved so every future bet (and every
              account) picks them up.
            </DialogDescription>
          </DialogHeader>

          {nameReviewBet && (() => {
            const legs = getNameReviewLegs(nameReviewBet);
            const renderPicker = (sideLabel, ourName, candidates, value, setValue, mode, setMode, testPrefix) => (
              <div className="space-y-2 p-3 rounded-lg bg-slate-800/40 border border-slate-700">
                <div className="text-xs uppercase tracking-wider text-slate-400">{sideLabel}</div>
                <div className="text-sm text-slate-200">
                  Is{" "}
                  <span className="text-white font-semibold">{ourName}</span>{" "}
                  the same as…
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className={`px-2 py-1 rounded ${mode === "dropdown" ? "bg-sky-600 text-white" : "bg-slate-700 text-slate-300"}`}
                    onClick={() => setMode("dropdown")}
                    data-testid={`${testPrefix}-mode-dropdown`}
                  >
                    Pick from page
                  </button>
                  <button
                    type="button"
                    className={`px-2 py-1 rounded ${mode === "typed" ? "bg-sky-600 text-white" : "bg-slate-700 text-slate-300"}`}
                    onClick={() => setMode("typed")}
                    data-testid={`${testPrefix}-mode-typed`}
                  >
                    Type Betway name
                  </button>
                </div>
                {mode === "dropdown" ? (
                  <select
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid={`${testPrefix}-dropdown`}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                  >
                    <option value="">— pick a Betway name —</option>
                    {candidates.map(n => (<option key={n} value={n}>{n}</option>))}
                    {!candidates.length && (<option value="" disabled>No games were captured — switch to typed input.</option>)}
                  </select>
                ) : (
                  <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Exact Betway spelling"
                    data-testid={`${testPrefix}-typed`}
                    className="bg-slate-900 border-slate-700 text-slate-100 text-sm"
                  />
                )}
              </div>
            );
            return (
              <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
                {legs.map((leg, i) => {
                  const games = leg.candidate_games || [];
                  const candidates = Array.from(new Set(
                    games.flatMap(g => [g.home, g.away]).filter(Boolean)
                  )).sort();
                  const sel = nameReviewLegSel[i] || { home: "", away: "", homeMode: "dropdown", awayMode: "dropdown" };
                  return (
                    <div key={i} className="space-y-2" data-testid={`name-review-leg-${i}`}>
                      {legs.length > 1 && (
                        <div className="flex items-center justify-between text-xs text-slate-300">
                          <span className="font-semibold text-sky-300">
                            Leg {i + 1}: {leg.home_team} vs {leg.away_team}
                          </span>
                          {leg.market_label && (
                            <span className="text-emerald-300">
                              {leg.market_label}{leg.pick ? ` / ${leg.pick}` : ""}
                            </span>
                          )}
                        </div>
                      )}
                      {renderPicker(
                        "Home",
                        leg.home_team,
                        candidates,
                        sel.home,
                        (v) => updateLegSel(i, { home: v }),
                        sel.homeMode,
                        (m) => updateLegSel(i, { homeMode: m }),
                        `name-review-home-${i}`
                      )}
                      {renderPicker(
                        "Away",
                        leg.away_team,
                        candidates,
                        sel.away,
                        (v) => updateLegSel(i, { away: v }),
                        sel.awayMode,
                        (m) => updateLegSel(i, { awayMode: m }),
                        `name-review-away-${i}`
                      )}
                      {!!games.length && (
                        <div className="text-xs text-slate-500 pt-1">
                          Betway listed {games.length} game{games.length > 1 ? "s" : ""} on that page.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <DialogFooter>
            <Button
              variant="outline"
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              onClick={() => setNameReviewBet(null)}
              disabled={nameReviewSubmitting}
              data-testid="name-review-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-sky-600 hover:bg-sky-700 text-white"
              onClick={confirmNameReview}
              disabled={nameReviewSubmitting}
              data-testid="name-review-confirm-btn"
            >
              {nameReviewSubmitting ? "Saving…" : "Save & retry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Mode status card for the Overview tab
// ──────────────────────────────────────────────────────────────
const ModeStatusCard = ({ icon, title, on, onLabel, offLabel, detail, testid }) => (
  <Card
    className={`bg-slate-900 ${on ? "border-emerald-500/40" : "border-slate-800"}`}
    data-testid={testid}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold">
          {icon} {title}
        </div>
        <Badge className={on ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700/60 text-slate-400"}>
          {on ? onLabel : offLabel}
        </Badge>
      </div>
      <div className="text-slate-400 text-xs mt-2">{detail}</div>
    </CardContent>
  </Card>
);

const FormNum = ({ label, value, onChange, step = 0.5, testid, min, max, disabled }) => (
  <div>
    <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
    <Input
      type="number"
      step={step}
      min={min}
      max={max}
      disabled={!!disabled}
      value={value ?? ""}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="bg-slate-950 border-slate-700 text-white disabled:opacity-50"
      data-testid={testid}
    />
  </div>
);


// ──────────────────────────────────────────────────────────────
// PnlChart — SVG sparkline of cumulative P&L across 24 UTC hours
// plus a compact hourly breakdown table underneath.
// ──────────────────────────────────────────────────────────────
const PnlChart = ({ buckets }) => {
  if (!buckets || buckets.length === 0) return null;
  const W = 1000, H = 160, PAD = 24;
  const vals = buckets.map(b => b.cum_pnl || 0);
  const maxAbs = Math.max(1, ...vals.map(v => Math.abs(v)));
  const yFor = v => H - PAD - ((v + maxAbs) / (2 * maxAbs)) * (H - 2 * PAD);
  const xFor = i => PAD + (i / (buckets.length - 1)) * (W - 2 * PAD);
  const pts = buckets.map((b, i) => `${xFor(i).toFixed(1)},${yFor(b.cum_pnl || 0).toFixed(1)}`).join(" ");
  const zeroY = yFor(0);
  const finalPnl = vals[vals.length - 1] || 0;
  const totalPnl = buckets.reduce((a, b) => a + (b.pnl || 0), 0);
  const totalStaked = buckets.reduce((a, b) => a + (b.staked || 0), 0);
  const totalReturned = buckets.reduce((a, b) => a + (b.returned || 0), 0);
  const fx = (n) => (Number(n) || 0).toFixed(2);
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="autopilot-pnl-chart">
      <CardHeader className="flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-emerald-300" />
          Daily P&amp;L <span className="text-slate-400 text-sm font-normal">(cum. by UTC hour)</span>
        </CardTitle>
        <div className="flex items-center gap-5 text-sm flex-wrap">
          <div className="text-slate-400">
            Staked <span className="text-amber-300 font-tabular" data-testid="pnl-chart-staked">${fx(totalStaked)}</span>
          </div>
          <div className="text-slate-400">
            Returned <span className="text-sky-300 font-tabular" data-testid="pnl-chart-returned">${fx(totalReturned)}</span>
          </div>
          <div className="text-slate-400">
            Realised <span className={`font-tabular ${totalPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="pnl-chart-realised">${fx(totalPnl)}</span>
          </div>
          <div className="text-slate-400">
            Final cum. <span className={`font-tabular ${finalPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="pnl-chart-final-cum">${fx(finalPnl)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40">
          <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} stroke="#334155" strokeDasharray="4 4" />
          <polyline
            points={`${PAD},${zeroY} ${pts} ${(W - PAD)},${zeroY}`}
            fill={finalPnl >= 0 ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)"}
            stroke="none"
          />
          <polyline points={pts} fill="none" stroke={finalPnl >= 0 ? "#10b981" : "#ef4444"} strokeWidth="2.5" />
          {buckets.map((b, i) => (i % 3 === 0 ? (
            <text key={i} x={xFor(i)} y={H - 4} fill="#64748b" fontSize="10" textAnchor="middle">
              {String(b.hour).padStart(2, "0")}h
            </text>
          ) : null))}
        </svg>
        <div className="overflow-x-auto">
          <table className="w-full text-xs mt-3">
            <thead className="text-slate-400">
              <tr className="border-b border-slate-800 text-left">
                <th className="p-1.5">Hr (UTC)</th>
                <th>Placed</th><th>W</th><th>L</th><th>Staked</th><th>Returned</th><th>Realised</th><th>Cum.</th>
              </tr>
            </thead>
            <tbody>
              {buckets.filter(b => b.placed || b.settled_won || b.settled_lost || b.settled_void || b.staked).map(b => (
                <tr key={b.hour} className="border-b border-slate-800/40" data-testid={`pnl-row-${b.hour}`}>
                  <td className="p-1.5 text-slate-400">{String(b.hour).padStart(2, "0")}:00</td>
                  <td className="text-slate-300">{b.placed}</td>
                  <td className="text-emerald-300">{b.settled_won}</td>
                  <td className="text-rose-300">{b.settled_lost}</td>
                  <td className="text-amber-300 font-tabular">${fx(b.staked)}</td>
                  <td className="text-sky-300 font-tabular">${fx(b.returned)}</td>
                  <td className={`font-tabular ${(b.pnl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>${fx(b.pnl)}</td>
                  <td className={`font-tabular ${(b.cum_pnl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>${fx(b.cum_pnl)}</td>
                </tr>
              ))}
              {!buckets.some(b => b.placed || b.staked) && (
                <tr><td colSpan={8} className="p-4 text-center text-slate-500">No activity yet today.</td></tr>
              )}
            </tbody>
            {buckets.some(b => b.placed || b.staked) && (
              <tfoot className="text-slate-300 font-semibold">
                <tr className="border-t border-slate-700">
                  <td className="p-1.5">Total</td>
                  <td>{buckets.reduce((a, b) => a + (b.placed || 0), 0)}</td>
                  <td className="text-emerald-300">{buckets.reduce((a, b) => a + (b.settled_won || 0), 0)}</td>
                  <td className="text-rose-300">{buckets.reduce((a, b) => a + (b.settled_lost || 0), 0)}</td>
                  <td className="text-amber-300 font-tabular">${fx(totalStaked)}</td>
                  <td className="text-sky-300 font-tabular">${fx(totalReturned)}</td>
                  <td className={`font-tabular ${totalPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>${fx(totalPnl)}</td>
                  <td className={`font-tabular ${finalPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>${fx(finalPnl)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
